import React from "react";
import {
  Box,
  Button,
  IconButton,
  Typography,
  createStyles,
  withStyles,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import FrequencyPill from "./FrequencyPill.web";
import TrashIcon from "./TrashIcon.web";

export interface AddonOption {
  id: number;
  name: string;
  priceLabel: string;
  frequencyLabel: string;
  priceNote?: string;
}

interface Props {
  addon: AddonOption;
  added: boolean;
  disabled?: boolean;
  onAdd: (id: number) => void;
  onRemove: (id: number) => void;
  classes: Record<string, string>;
}

const styles = (theme: any) =>
  createStyles({
    card: {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 12,
      padding: "1.25rem",
      marginBottom: "1rem",
    },
    cardAdded: {
      borderColor: theme.palette.primary.main,
    },
    footer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginTop: "0.75rem",
    },
    price: {
      display: "flex",
      alignItems: "center",
      gap: "0.5rem",
    },
    name: {
      fontSize: "1.375rem",
      fontWeight: 600,
      lineHeight: 1.35,
    },
    priceValue: {
      fontSize: "1.5rem",
      fontWeight: 700,
    },
    priceNote: {
      marginTop: "0.25rem",
      fontSize: "0.9375rem",
      color: theme.palette.text.secondary,
    },
    added: {
      display: "flex",
      alignItems: "center",
      gap: "0.5rem",
    },
    addedChip: {
      display: "flex",
      alignItems: "center",
      gap: "0.5rem",
      backgroundColor: "#C9F2DD",
      color: "#0B8043",
      borderRadius: 8,
      padding: "0.5rem 1rem",
      fontSize: "1rem",
      fontWeight: 500,
    },
    addService: {
      borderRadius: 8,
      padding: "0.5rem 1rem",
      fontSize: "1rem",
      textTransform: "none",
    },
    removeButton: {
      color: "#E53935",
    },
  });

const AddonServiceCard = ({
  addon,
  added,
  disabled,
  onAdd,
  onRemove,
  classes,
}: Props) => (
  <Box className={added ? `${classes.card} ${classes.cardAdded}` : classes.card}>
    <Typography className={classes.name}>{addon.name}</Typography>
    <Box className={classes.footer}>
      <Box>
        <Box className={classes.price}>
          <Typography className={classes.priceValue}>{addon.priceLabel}</Typography>
          <FrequencyPill label={addon.frequencyLabel} />
        </Box>
        {addon.priceNote && (
          <Typography variant="body2" className={classes.priceNote}>
            {addon.priceNote}
          </Typography>
        )}
      </Box>
      {added ? (
        <Box className={classes.added}>
          <Box className={classes.addedChip}>
            <CheckCircleOutlineIcon fontSize="small" />
            <Typography color="inherit">Added</Typography>
          </Box>
          <IconButton
            aria-label={`Remove ${addon.name}`}
            className={classes.removeButton}
            onClick={() => onRemove(addon.id)}
            disabled={disabled}
          >
            <TrashIcon />
          </IconButton>
        </Box>
      ) : (
        <Button
          variant="outlined"
          className={classes.addService}
          startIcon={<AddIcon />}
          onClick={() => onAdd(addon.id)}
          disabled={disabled}
        >
          Add service
        </Button>
      )}
    </Box>
  </Box>
);

export default withStyles(styles)(AddonServiceCard);
